import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col, Form } from 'antd';
import { CategoriesAPI } from 'src/modules/categories/api/CategoriesAPI';
import { SelectWithPagination } from 'src/components/SelectWithPagination';
import { useTranslation } from 'react-i18next';
import { textFrom } from 'src/utils/textFrom';

const categoryToOption = item => ({ value: item.key, label: item.label });

const asDefaultOptions = category => (
  category?.key && category?.label ? [{ value: category.key, label: category.label }] : []
);

export const CategoriesFood = ({ form, editMode, initialMacroCategory, initialMicroCategory, language }) => {
  const { t } = useTranslation();
  const text = textFrom('components.categoriesFood', t);

  const [selectedMacro, setSelectedMacro] = useState(initialMacroCategory?.key || '');
  const [macroOptions, setMacroOptions] = useState([]);
  const [macroPagination, setMacroPagination] = useState({});
  const [microOptions, setMicroOptions] = useState([]);
  const [microPagination, setMicroPagination] = useState({});
  const FOOD_PARENT_KEY = 'food';

  useEffect(() => {
    if (initialMacroCategory?.key) {
      setSelectedMacro(initialMacroCategory.key);
    }
  }, [initialMacroCategory?.key]);

  const getMacroCategoriesAtPage = useCallback(async (page) => {
    const res = await CategoriesAPI.getMacroCategories(page, FOOD_PARENT_KEY, language);
    setMacroOptions(res?.data?.data?.items || []);
    setMacroPagination({ ...res?.data?.data });
  }, [language]);

  const getMicroCategoriesAtPage = useCallback(async (page) => {
    if (!selectedMacro) {
      setMicroOptions([]);
      setMicroPagination({});
      return;
    }
    const res = await CategoriesAPI.getMicroCategories(selectedMacro, page, language);
    setMicroOptions(res?.data?.data?.items || []);
    setMicroPagination({ ...res?.data?.data });
  }, [selectedMacro, language]);

  return (
    <Row gutter={[16, 16]}>
      <Col span={8}>
        <Form.Item
          name={editMode ? ['categories', 'macro'] : ['categories', 'macro', 'key']}
          label={text('macro')}
          rules={[
            {
              required: true,
              message: text('macroRequired')
            }
          ]}
        >
          <SelectWithPagination
            key={language}
            searchable={false}
            items={macroOptions}
            getItemsAtPage={getMacroCategoriesAtPage}
            pagination={macroPagination}
            toOption={categoryToOption}
            defaultOptions={asDefaultOptions(initialMacroCategory)}
            placeholder={text('select')}
            onChange={(val) => {
              setSelectedMacro(val);
              form.setFieldsValue({
                categories: {
                  micro: editMode ? null : { key: null }
                }
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item
          name={editMode ? ['categories', 'micro'] : ['categories', 'micro', 'key']}
          label={text('micro')}
          shouldUpdate={(prevValues, currValues) => (editMode ? currValues.categories.macro : currValues.categories.macro.key)}
          rules={[
            {
              required: true,
              message: text('microRequired')
            }
          ]}
        >
          <SelectWithPagination
            key={`${selectedMacro}-${language}`}
            searchable={false}
            items={microOptions}
            getItemsAtPage={getMicroCategoriesAtPage}
            pagination={microPagination}
            toOption={categoryToOption}
            defaultOptions={selectedMacro === initialMacroCategory?.key ? asDefaultOptions(initialMicroCategory) : []}
            placeholder={text('select')}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};
